// frontend/src/components/layout/Layout.jsx
// Signal workspace shell: sidebar (full, icon rail or mobile drawer), 52 px
// top bar, content, the command palette and the call dock.

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { useMediaQuery } from "../../hooks";
import { useSip } from "../../contexts/SipContext";
import { KeyboardShortcuts } from "../help/KeyboardShortcuts";
import { useAuth } from "../../contexts/AuthContext";
import { useUserRole } from "../../contexts/UserRoleContext";
import SkipNavigation from "../ui/SkipNavigation";
import CommandPalette from "../shell/CommandPalette";
import CallDock from "../shell/CallDock";
import SignalToaster from "../ui/SignalToaster";
import HintLayer from "../ui/HintLayer";
import Aurora from "../shell/Aurora";
import AskNexusPanel from "../assistant/AskNexusPanel";
import { WorkspaceBanner } from "../shell/WorkspaceSwitcher";
import { InShellContext } from "../shell/shellContext";
import { EASE, MS } from "../ui/motion";
import "./workspace.css";

const RAIL_KEY = "nexusDialer.sidebarRail";

// Browser storage can throw (private mode, blocked site data); the rail
// preference is a convenience, so failures fall back to the viewport default.
const readRail = (key) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? null : value === "1";
  } catch {
    return null;
  }
};

const writeRail = (key, value) => {
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* preference only */
  }
};

/**
 * The part of a path that names a page, for the route transition: a tab or
 * a query change inside one page (/reports → /reports/calls, ?status=…)
 * keeps its key and does not replay the entrance; opening a record
 * (/contacts/42), moving to another record or another section does.
 */
export const routeFamily = (pathname = "/") => {
  const [first = "", second = ""] = pathname.split("/").filter(Boolean);
  const isRecord =
    /^\d+$/.test(second) || /^[0-9a-f-]{8,}$/i.test(second) || second === "new";
  return isRecord ? `${first}/${second}` : first;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Route content enters with a 6 px rise and fade on --sg-slow (DESIGN.md
 * "Motion": route change). It runs on the content region only, through the
 * Web Animations API so the page itself never remounts for it; the shell
 * (sidebar, top bar, dock) never moves. Reduced motion skips it.
 */
const useRouteEntrance = (ref, family) => {
  useLayoutEffect(() => {
    const element = ref.current;
    if (
      !element ||
      typeof element.animate !== "function" ||
      prefersReducedMotion()
    )
      return undefined;
    const animation = element.animate(
      [
        { opacity: 0, transform: "translateY(6px)" },
        { opacity: 1, transform: "none" },
      ],
      {
        duration: MS.moment,
        easing: `cubic-bezier(${EASE.standard.join(",")})`,
      },
    );
    return () => animation.cancel();
  }, [ref, family]);
};

const isTypingTarget = (target) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

const Layout = ({ children }) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  // 768–1279 px defaults to the icon rail (DESIGN.md "Responsive").
  const isMedium = useMediaQuery("(max-width: 1279px)");
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Create a reference for the audio element used by SIP calls
  const audioRef = useRef(null);
  const { setAudioElement } = useSip();
  const auth = useAuth();
  const { role } = useUserRole() || {};
  const location = useLocation();
  const family = routeFamily(location.pathname);
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  const bannerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useRouteEntrance(innerRef, family);

  // A new page starts at its top; a tab or filter change keeps the scroll.
  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    setScrolled(false);
  }, [family]);

  // The top bar gains its scroll edge once content runs under it.
  const onContentScroll = useCallback((event) => {
    const next = event.currentTarget.scrollTop > 0;
    setScrolled((current) => (current === next ? current : next));
  }, []);

  // Floating layers that start below the top bar (Ask Nexus) need the demo
  // banner's height, which wraps on narrow screens.
  const demo = Boolean(auth?.user?.demo);
  const impersonating = Boolean(auth?.user?.impersonating);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const banner = bannerRef.current;
    if (!banner) {
      root.style.removeProperty("--sg-banner-h");
      return undefined;
    }
    const apply = () =>
      root.style.setProperty("--sg-banner-h", `${banner.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === "undefined")
      return () => root.style.removeProperty("--sg-banner-h");
    const observer = new ResizeObserver(apply);
    observer.observe(banner);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--sg-banner-h");
    };
  }, [demo, impersonating]);

  // The rail choice is remembered per user.
  const railKey = `${RAIL_KEY}:${auth?.user?.id ?? "anon"}`;
  const [railPreference, setRailPreference] = useState(() => readRail(railKey));
  useEffect(() => {
    setRailPreference(readRail(railKey));
  }, [railKey]);
  const collapsed = railPreference ?? isMedium;

  const toggleCollapsed = useCallback(() => {
    setRailPreference(() => {
      const next = !collapsed;
      writeRail(railKey, next);
      return next;
    });
  }, [collapsed, railKey]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Update sidebar state when screen size changes
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Pass audio element to SIP context when it's available
  useEffect(() => {
    if (audioRef.current) {
      setAudioElement(audioRef.current);
    }
  }, [audioRef.current, setAudioElement]);

  // Escape closes the mobile drawer; "[" toggles the rail on desktop.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isMobile && sidebarOpen) {
        setSidebarOpen(false);
      } else if (
        e.key === "[" &&
        !isMobile &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !paletteOpen &&
        !isTypingTarget(e.target)
      ) {
        toggleCollapsed();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, sidebarOpen, paletteOpen, toggleCollapsed]);

  return (
    <InShellContext.Provider value>
      <div
        className="nx-app nx-workspace sg-shell"
        data-rail={collapsed && !isMobile ? "true" : undefined}
      >
        <Aurora />
        <SkipNavigation />
        {/* Full width above both columns (workspace.css grid), so the brand
          row and the top bar share one baseline. */}
        <WorkspaceBanner ref={bannerRef} user={auth?.user} />
        <Sidebar
          open={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />

        <div className="sg-main">
          <Navbar
            toggleSidebar={toggleSidebar}
            isMobile={isMobile}
            sidebarOpen={sidebarOpen}
            scrolled={scrolled}
            onOpenPalette={() => setPaletteOpen(true)}
          />
          <main
            id="main-content"
            ref={contentRef}
            tabIndex={-1}
            className="nx-workspace-content sg-content"
            onScroll={onContentScroll}
          >
            <div
              ref={innerRef}
              className="sg-content-inner"
              data-route={family}
            >
              {children}
            </div>
          </main>

          <KeyboardShortcuts
            showTrigger={false}
            role={role}
            user={auth?.user}
          />
        </div>

        <CallDock />
        {/* The only mounted toaster: a fixed column at the bottom right, above
          the call dock (--sg-dock-clear), clear of the top bar's pill and
          the page's primary action. */}
        <SignalToaster />
        <HintLayer />
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <AskNexusPanel />

        {/* Hidden audio element for SIP calls */}
        <audio
          ref={audioRef}
          id="sip-audio"
          className="hidden"
          autoPlay
          playsInline
        />
      </div>
    </InShellContext.Provider>
  );
};

export default Layout;
